/**
 * OIM CSV template generators.
 *
 * Templates intentionally OMIT the site_id column. The user picks a site from
 * the SiteSelector before uploading; the backend stamps every row with that
 * site_id (via the required `site_id` query param on /api/oim/ingest/<type>).
 * This way the same template works for every site — no UUID hunting needed.
 *
 * Date semantics:
 *   - The "reference date" is the day before today (UTC), matching the spec's
 *     §3.1 example timestamp 2026-04-28T10:00:00Z.
 *   - Energy + Production: 24 hourly rows for the reference date.
 *   - Cost: 3 daily rows ending on the reference date (older are is_actual=true,
 *     most recent is provisional).
 */

const pad2 = (n) => String(n).padStart(2, '0');

const yesterdayUtc = () => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  d.setUTCHours(0, 0, 0, 0);
  return d;
};

const toIsoZ = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}:00:00Z`;

const toIsoDate = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

// Deterministic-ish per-hour synthetic values for an ethanol distillery line.
// Each value uses the meter's NATURAL unit — the backend's compute engine
// converts to kWh-equivalent at aggregation time using ENERGY_UNIT_TO_KWH.
//   electricity: kWh         (electrical meters report kWh directly)
//   steam:       tonnes      (boiler meters report mass; ~700 kWh/T thermal)
//   bagasse:     tonnes      (the typical Indian distillery boiler fuel; ~2.9 kWh/kg LHV)
//   production:  kL ethanol
//   water:       kL          (cooling + process water consumption)
//   waste:       kL          (vinasse / spent wash — the dominant distillery waste stream)
//
// Resulting energy intensity ≈ 28–35 kWh/kL — typical for an ethanol distillery.
// Resulting water intensity ≈ 8-12 kL/kL — typical industry range for ethanol.
// Resulting waste intensity ≈ 10-15 kL/kL — typical vinasse-to-ethanol ratio.
const hourSample = (hour) => {
  const electricity = 1000 + ((hour * 37) % 350);          // 1000..1349 kWh
  const steam       =    0.40 + ((hour * 13) % 9) / 100;   // 0.40..0.48 tonnes generated at boiler outlet
  // Split-meter mode: process-line steam_consumed adds up to ~88% of generation,
  // leaving ~12% as system losses (traps, distribution, vents) — typical for
  // a 10-bar industrial steam network.
  const steamConsumedA = steam * 0.55;                     // process line A inlet meter
  const steamConsumedB = steam * 0.33;                     // process line B inlet meter
  // Condensate returned to feedwater tank — typical 40-60% recovery on a
  // distillery boiler. Reported in kL (≡ tonnes for water).
  const condensate     = steam * 0.45;
  const bagasse     =    0.10 + ((hour * 7)  % 5) / 100;   // 0.10..0.14 tonnes (290..406 kWh-equiv)
  const rice_husk   =    0.05 + ((hour * 11) % 6) / 100;   // 0.05..0.10 tonnes (175..350 kWh-equiv) — paddy mills + ethanol plants co-firing
  const spent_wash  =    0.08 + ((hour * 9)  % 7) / 100;   // 0.08..0.14 tonnes (288..504 kWh-equiv) — ZLD MEE concentrate co-fired
  const production  =   45 + ((hour * 11) % 12);           // 45..56 kL
  const water       =  450 + ((hour * 23) % 80);           // 450..530 kL  (~10x production)
  const waste       =  500 + ((hour * 29) % 90);           // 500..590 kL  (~12x production — vinasse)
  // ZLD train: vinasse fed to MEE-RO; ~92% recovered as water, balance becomes
  // concentrated solids for incineration.
  const zldInput     = waste;
  const zldRecovered = waste * 0.92;
  return { electricity, steam, steamConsumedA, steamConsumedB, condensate,
           bagasse, rice_husk, spent_wash, production, water, waste,
           zldInput, zldRecovered };
};

// site_id is intentionally NOT in any header — it's applied at upload time
// from the SiteSelector via the /api/oim/ingest endpoint's query param.
// line_id is also omitted from templates — the backend accepts it as an
// optional column for sites that need per-production-line breakdown, but
// most deployments don't, and an empty column adds noise. To use line_id,
// add the column manually to a template before upload.
export const ENERGY_HEADER =
  'timestamp,energy_type,energy_value,unit,source,quality_flag,notes';

export const PRODUCTION_HEADER =
  'timestamp,product,production_value,unit,source,estimation_flag,notes';

export const COST_HEADER =
  'date,product,energy_cost_per_unit,raw_material_cost,total_cost,currency,is_actual';

export const WATER_HEADER =
  'timestamp,water_type,water_value,unit,source,quality_flag,notes';

export const WASTE_HEADER =
  'timestamp,waste_type,waste_value,unit,is_hazardous,disposal_method,source,quality_flag,notes';

export function buildEnergyTemplate() {
  const start = yesterdayUtc();
  const lines = [ENERGY_HEADER];
  for (let h = 0; h < 24; h++) {
    const ts = new Date(start);
    ts.setUTCHours(h);
    const tsStr = toIsoZ(ts);
    const { electricity, steam, steamConsumedA, steamConsumedB, bagasse,
            rice_husk, spent_wash } = hourSample(h);
    // Each row uses its meter's natural unit. The compute engine converts
    // (steam tonne → kWh × 700; bagasse tonne → kWh × 2900; rice_husk × 3500;
    // spent_wash × 3600) before computing energy intensity.
    // See backend/services/oim_compute.py for the full ENERGY_UNIT_TO_KWH table.
    // Other supported types: diesel, petrol, furnace_oil, gas, lpg, coal,
    // biomass, wood, renewable. See OIM help for the full table.
    lines.push(`${tsStr},electricity,${electricity.toFixed(2)},kWh,SCADA,raw,`);
    // Steam metering: use 'steam_generated' for the boiler-outlet meter and
    // 'steam_consumed' for each process-line inlet meter. The Steam dashboard
    // computes losses = generated − consumed. For tenants without per-line
    // metering, a single 'steam' row (legacy) still works.
    lines.push(`${tsStr},steam_generated,${steam.toFixed(2)},tonne,SCADA,raw,Boiler outlet meter`);
    lines.push(`${tsStr},steam_consumed,${steamConsumedA.toFixed(2)},tonne,SCADA,raw,Process line A inlet`);
    lines.push(`${tsStr},steam_consumed,${steamConsumedB.toFixed(2)},tonne,SCADA,raw,Process line B inlet`);
    lines.push(`${tsStr},bagasse,${bagasse.toFixed(3)},tonne,SCADA,raw,`);
    lines.push(`${tsStr},rice_husk,${rice_husk.toFixed(3)},tonne,SCADA,raw,Co-fired biomass`);
    lines.push(`${tsStr},spent_wash,${spent_wash.toFixed(3)},tonne,SCADA,raw,ZLD MEE concentrate`);
  }
  return lines.join('\n') + '\n';
}

export function buildWaterTemplate() {
  const start = yesterdayUtc();
  const lines = [WATER_HEADER];
  for (let h = 0; h < 24; h++) {
    const ts = new Date(start);
    ts.setUTCHours(h);
    const { water, condensate, zldRecovered } = hourSample(h);
    // Split the hourly water across categories so the row pattern is
    // educational. ~70% process water, ~30% cooling water.
    const processWater = (water * 0.7).toFixed(1);
    const coolingWater = (water * 0.3).toFixed(1);
    lines.push(`${toIsoZ(ts)},process,${processWater},kL,meter,raw,`);
    lines.push(`${toIsoZ(ts)},cooling,${coolingWater},kL,meter,raw,`);
    // Boiler condensate return — recovered hot water routed back to the
    // feedwater tank. The Steam dashboard divides this by steam_generated to
    // compute Condensate Recovery %.
    lines.push(`${toIsoZ(ts)},condensate,${condensate.toFixed(2)},kL,meter,raw,Feedwater-tank return`);
    // ZLD recovered water — output of MEE evaporator + RO train. Divided by
    // waste_type='zld_input' on the Water dashboard to compute ZLD Recovery %.
    lines.push(`${toIsoZ(ts)},zld_recovered,${zldRecovered.toFixed(1)},kL,meter,raw,MEE+RO output`);
  }
  return lines.join('\n') + '\n';
}

export function buildWasteTemplate() {
  const start = yesterdayUtc();
  const lines = [WASTE_HEADER];
  for (let h = 0; h < 24; h++) {
    const ts = new Date(start);
    ts.setUTCHours(h);
    const { waste, zldInput } = hourSample(h);
    // Vinasse (spent wash) is the dominant distillery waste stream — typically
    // 10-15× the volume of ethanol produced. ETP-bound, high BOD/COD load.
    lines.push(`${toIsoZ(ts)},vinasse,${waste.toFixed(1)},kL,false,etp_treatment,plant,raw,`);
    // Vinasse volume entering the ZLD evaporator-RO train. Acts as denominator
    // for ZLD Recovery % on the Water dashboard.
    lines.push(`${toIsoZ(ts)},zld_input,${zldInput.toFixed(1)},kL,false,zld_treatment,plant,raw,Fed to MEE+RO`);
  }
  return lines.join('\n') + '\n';
}

export function buildProductionTemplate() {
  const start = yesterdayUtc();
  const lines = [PRODUCTION_HEADER];
  for (let h = 0; h < 24; h++) {
    const ts = new Date(start);
    ts.setUTCHours(h);
    const { production } = hourSample(h);
    // Mark the latest 4 hours as estimated to demonstrate the §11 reconciliation
    // path (estimates get overwritten when actuals land at end-of-day).
    const isEstimate = h >= 20 ? 'true' : 'false';
    lines.push(`${toIsoZ(ts)},ethanol,${production.toFixed(2)},kL,MES,${isEstimate},`);
  }
  return lines.join('\n') + '\n';
}

export function buildCostTemplate() {
  const ref = yesterdayUtc();
  const lines = [COST_HEADER];
  // 3 days ending on the reference date — older two are actual, latest is provisional
  for (let i = 2; i >= 0; i--) {
    const d = new Date(ref);
    d.setUTCDate(ref.getUTCDate() - i);
    const ds = toIsoDate(d);
    const energyCostPerUnit = 6.5;
    const rawMaterialCost = 32000 + i * 250;
    const totalCost = 50000 + i * 500;
    const isActual = i > 0 ? 'true' : 'false';
    lines.push(`${ds},ethanol,${energyCostPerUnit},${rawMaterialCost},${totalCost},INR,${isActual}`);
  }
  return lines.join('\n') + '\n';
}

export function templateFor(fileType) {
  switch (fileType) {
    case 'energy':     return { content: buildEnergyTemplate(),     filename: 'oim_energy_template.csv' };
    case 'production': return { content: buildProductionTemplate(), filename: 'oim_production_template.csv' };
    case 'cost':       return { content: buildCostTemplate(),       filename: 'oim_cost_template.csv' };
    case 'water':      return { content: buildWaterTemplate(),      filename: 'oim_water_template.csv' };
    case 'waste':      return { content: buildWasteTemplate(),      filename: 'oim_waste_template.csv' };
    default: throw new Error(`Unknown OIM template type: ${fileType}`);
  }
}

export function downloadTemplate(fileType) {
  const { content, filename } = templateFor(fileType);
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Defer revoke so Safari can finish the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
